import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Edit3, Trash2, Shield, X, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const emptyForm = () => ({ name: "", email: "", password: "", permissions: ["orders.view", "orders.update_status"] });

export default function AdminManagers() {
  const [managers, setManagers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    const [m, c] = await Promise.all([
      api.get("/admin/managers"),
      api.get("/admin/permissions-catalog"),
    ]);
    setManagers(m.data);
    setCatalog(c.data);
  };
  useEffect(() => { load(); }, []);

  const onSave = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, permissions: form.permissions };
        if (form.password) payload.password = form.password;
        await api.patch(`/admin/managers/${editing.id}`, payload);
        toast.success("Manager updated");
      } else {
        if (!form.email || !form.password || !form.name) return toast.error("All fields required");
        await api.post("/admin/managers", form);
        toast.success("Manager created");
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const onEdit = (m) => {
    setEditing(m);
    setForm({ name: m.name, email: m.email, password: "", permissions: m.permissions || [] });
    setOpen(true);
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this manager?")) return;
    await api.delete(`/admin/managers/${id}`);
    toast.success("Manager removed");
    load();
  };
  const togglePerm = (key) => {
    setForm((f) => ({ ...f, permissions: f.permissions.includes(key) ? f.permissions.filter((p) => p !== key) : [...f.permissions, key] }));
  };
  const toggleActive = async (m) => {
    await api.patch(`/admin/managers/${m.id}`, { active: !m.active });
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-managers-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-bold text-2xl md:text-3xl text-[var(--dd-green)]">Managers</h1>
          <p className="text-[var(--dsf-text-muted)] text-sm mt-1">Create staff accounts with custom permissions</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyForm()); }}}>
          <DialogTrigger asChild>
            <button className="btn-mustard !py-2.5" data-testid="add-manager-button"><UserPlus size={14} /> New manager</button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-bold text-2xl text-[var(--dd-green)]">{editing ? "Edit" : "New"} manager</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dd-green)]">Full name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="manager-form-name" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dd-green)]">Email</Label>
                  <Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 disabled:opacity-50" data-testid="manager-form-email" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dd-green)]">{editing ? "New password (leave blank to keep)" : "Password"}</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" data-testid="manager-form-password" />
                </div>
              </div>

              <div>
                <Label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dd-green)] flex items-center gap-2 mb-3"><Shield size={12} /> Permissions</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="manager-perms-grid">
                  {catalog.map((p) => {
                    const checked = form.permissions.includes(p.key);
                    return (
                      <label key={p.key} className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition ${checked ? "border-[var(--dd-green)] bg-[var(--dd-green-soft)]" : "border-[var(--dsf-border)] hover:border-[var(--dd-green)]"}`} data-testid={`manager-perm-${p.key}`}>
                        <input type="checkbox" checked={checked} onChange={() => togglePerm(p.key)} className="accent-[var(--dd-green)]" />
                        <div>
                          <div className="text-sm font-medium text-[var(--dsf-text)]">{p.label}</div>
                          <div className="text-[10px] text-[var(--dsf-text-muted)] font-mono">{p.key}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 sticky bottom-0 bg-white pt-3 border-t border-[var(--dsf-border)]">
              <button onClick={() => setOpen(false)} className="btn-pill-outline !py-2 !px-5">Cancel</button>
              <button onClick={onSave} className="btn-mustard !py-2" data-testid="manager-form-submit">{editing ? "Update" : "Create"}</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[var(--dsf-border)] rounded-2xl overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-3 bg-[var(--dd-bone)] px-4 py-3 text-[10px] uppercase tracking-[0.22em] text-[var(--dd-green)] font-bold">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-3">Permissions</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {managers.length === 0 && <div className="text-center py-12 text-[var(--dsf-text-muted)]">No managers yet — create one</div>}
        {managers.map((m) => (
          <div key={m.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 px-4 py-4 border-t border-[var(--dsf-border)] items-center" data-testid={`manager-row-${m.id}`}>
            <div className="md:col-span-3 font-semibold text-[var(--dd-green)]">{m.name}</div>
            <div className="md:col-span-3 text-sm text-[var(--dsf-text-secondary)] truncate">{m.email}</div>
            <div className="md:col-span-3 flex flex-wrap gap-1">
              {(m.permissions || []).slice(0, 3).map((p) => (
                <span key={p} className="text-[9px] bg-[var(--dd-green-soft)] text-[var(--dd-green)] px-2 py-0.5 rounded-full font-mono">{p}</span>
              ))}
              {(m.permissions || []).length > 3 && <span className="text-[9px] text-[var(--dsf-text-muted)]">+{m.permissions.length - 3}</span>}
            </div>
            <div className="md:col-span-2">
              <button onClick={() => toggleActive(m)} className={`text-xs font-semibold px-3 py-1 rounded-full ${m.active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid={`manager-toggle-${m.id}`}>
                {m.active ? "Active" : "Disabled"}
              </button>
            </div>
            <div className="md:col-span-1 flex md:justify-end gap-2">
              <button onClick={() => onEdit(m)} className="w-8 h-8 rounded-full border border-[var(--dsf-border)] flex items-center justify-center text-[var(--dd-green)] hover:bg-[var(--dd-bone)]" data-testid={`manager-edit-${m.id}`}><Edit3 size={13} /></button>
              <button onClick={() => onDelete(m.id)} className="w-8 h-8 rounded-full border border-[var(--dsf-border)] flex items-center justify-center text-[var(--dd-coral)] hover:bg-[var(--dd-bone)]" data-testid={`manager-delete-${m.id}`}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
